"use client";
import _extends from '@babel/runtime/helpers/extends';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import * as React from 'react';
import { Box } from '../Box/Box.web.js';
import { IconButton } from '../IconButton/IconButton.web.js';
import { DatePicker } from '../DatePicker/DatePicker.web.js';
import { button } from './styles.js';
import { prevDate, nextDate, isPrevDisabled, isNextDisabled } from './utils.js';
import { ThemeProvider } from 'styled-components';
import { Popover } from '../Popover/Popover.web.js';
import { DateBrowserYearSelector } from './DateBrowserYearSelector.web.js';
import { format } from 'date-fns';
import { usePopover } from '../Popover/utils.web.js';
import { IconChevronRight } from '../icons/ui_navigation/IconChevronRight.web.js';
import { IconCalendarUI } from '../icons/ui_general/IconCalendarUI.web.js';
import { useTheme } from '../utils/theming/useTheme.js';
import { IconChevronLeft } from '../icons/ui_navigation/IconChevronLeft.web.js';
import { spacing } from '../constants/spacing.js';

var _excluded = ["color", "dateFormat", "locale", "onChange", "mode", "minDate", "maxDate", "selected", "label", "prevButtonAriaLabel", "nextButtonAriaLabel", "size"];
var DateBrowserTrigger = /*#__PURE__*/React.forwardRef(function DateBrowserTriggerComponent(_ref, ref) {
  var color = _ref.color,
    value = _ref.value,
    size = _ref.size,
    italic = _ref.italic,
    fontWeight = _ref.fontWeight,
    onClick = _ref.onClick;
  return /*#__PURE__*/React.createElement(button, {
    ref: ref,
    "data-testid": "elemental-DateBrowser-button",
    "aria-label": value,
    $status: "plain",
    $size: size,
    $italic: italic,
    $weight: fontWeight,
    $color: color,
    onClick: onClick
  }, /*#__PURE__*/React.createElement(Box, {
    display: "flex",
    alignItems: "center",
    height: "100%"
  }, /*#__PURE__*/React.createElement(IconCalendarUI, {
    mr: spacing.xxxs
  }), value));
});
var getDatePickerModeProps = function getDatePickerModeProps(mode, dateFormat) {
  switch (mode) {
    case 'month':
      {
        return {
          showMonthYearPicker: true,
          showFullMonthYearPicker: true,
          dateFormat: dateFormat || 'LLLL yyyy'
        };
      }
    case 'year':
      {
        return {
          dateFormat: dateFormat || 'yyyy'
        };
      }
    default:
      {
        return {
          dateFormat: dateFormat || 'dd.MM.yyyy'
        };
      }
  }
};
var DateBrowser = function DateBrowser(props) {
  var colorProp = props.color,
    dateFormat = props.dateFormat,
    locale = props.locale,
    onChange = props.onChange,
    _props$mode = props.mode,
    mode = _props$mode === undefined ? 'date' : _props$mode,
    minDate = props.minDate,
    maxDate = props.maxDate,
    _props$selected = props.selected,
    selected = _props$selected === undefined ? new Date() : _props$selected,
    _props$label = props.label,
    label = _props$label === undefined ? 'Select a date' : _props$label,
    _props$prevButtonAria = props.prevButtonAriaLabel,
    prevButtonAriaLabel = _props$prevButtonAria === undefined ? 'Previous' : _props$prevButtonAria,
    _props$nextButtonAria = props.nextButtonAriaLabel,
    nextButtonAriaLabel = _props$nextButtonAria === undefined ? 'Next' : _props$nextButtonAria,
    _props$size = props.size,
    size = _props$size === undefined ? 'm' : _props$size,
    rest = _objectWithoutProperties(props, _excluded);
  var datePickerProps = getDatePickerModeProps(mode, dateFormat);
  var handlePreviousClick = React.useCallback(function () {
    onChange(prevDate(mode, selected));
  }, [selected, onChange, mode]);
  var handleNextClick = React.useCallback(function () {
    onChange(nextDate(mode, selected));
  }, [selected, onChange, mode]);
  var isPrevButtonDisabled = isPrevDisabled(minDate, mode, selected);
  var isNextButtonDisabled = isNextDisabled(maxDate, mode, selected);
  var _usePopover = usePopover(),
    open = _usePopover.open,
    anchor = _usePopover.anchor,
    handleOpen = _usePopover.handleOpen,
    handleClose = _usePopover.handleClose;
  var theme = useTheme();
  var dateBrowserTheme = theme.datebrowser.web;
  var color = colorProp || dateBrowserTheme.color;
  return /*#__PURE__*/React.createElement(ThemeProvider, {
    theme: theme
  }, /*#__PURE__*/React.createElement(Box, _extends({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between"
  }, rest), /*#__PURE__*/React.createElement(IconButton, {
    "aria-label": prevButtonAriaLabel,
    status: "plain",
    disabled: isPrevButtonDisabled,
    onClick: handlePreviousClick,
    color: color,
    icon: /*#__PURE__*/React.createElement(IconChevronLeft, null),
    borderRadius: dateBrowserTheme.navigationButtons.borderRadius
  }), mode === 'year' ? /*#__PURE__*/React.createElement(Box, {
    position: "relative"
  }, /*#__PURE__*/React.createElement(DateBrowserTrigger, {
    color: color,
    onClick: handleOpen,
    value: format(selected, datePickerProps.dateFormat),
    size: size,
    label: label
  }), /*#__PURE__*/React.createElement(Popover, {
    id: "elemental-example-popover1",
    onClose: handleClose,
    opened: open,
    anchor: anchor,
    anchorPos: "bottom",
    backgroundColor: theme.colors.background,
    fallbackPlacements: ['bottom'],
    padding: 0,
    paddingTop: spacing.xxxs,
    paddingBottom: spacing.xxxs,
    disablePortal: true,
    disableShift: true
  }, /*#__PURE__*/React.createElement(DateBrowserYearSelector, {
    minDate: minDate,
    maxDate: maxDate,
    selected: selected,
    onSelect: onChange,
    onClose: handleClose,
    color: color
  }))) : /*#__PURE__*/React.createElement(DatePicker, _extends({}, datePickerProps, {
    label: label,
    selected: selected,
    selectsRange: false,
    selectsMultiple: false,
    color: color,
    name: "elemental-DateBrowser-DatePicker",
    locale: locale,
    onChange: onChange,
    customInput: /*#__PURE__*/React.createElement(DateBrowserTrigger, {
      value: format(selected, datePickerProps.dateFormat),
      color: color,
      size: size,
      label: label
    }),
    minDate: minDate,
    maxDate: maxDate
  })), /*#__PURE__*/React.createElement(IconButton, {
    "aria-label": nextButtonAriaLabel,
    status: "plain",
    disabled: isNextButtonDisabled,
    onClick: handleNextClick,
    color: color,
    icon: /*#__PURE__*/React.createElement(IconChevronRight, null),
    borderRadius: dateBrowserTheme.navigationButtons.borderRadius
  })));
};

export { DateBrowser };
